import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import type {
  AddFeedToExperienceMutation,
  AddFeedToExperienceMutationVariables,
} from './useAddFeedToExperience.generated';

const query = gql`
  mutation AddFeedToExperience($input: AddFeedToExperienceInput!) {
    addFeedToExperience(input: $input) {
      kind
      id
      title
      originalTitle
      color
      syncedAt
      error
    }
  }
`;

export const useAddFeedToExperience = () => {
  const queryClient = useQueryClient();
  const { experience } = usePageExperienceContext();

  const { mutate, ...rest } = useMutation<
    AddFeedToExperienceMutation,
    QueryError,
    AddFeedToExperienceMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        AddFeedToExperienceMutation,
        AddFeedToExperienceMutationVariables
      >(query, variables),
    // The experience query key is a prefix of its events key, so this single
    // invalidation refreshes both experience.feeds and the sourced events the
    // server materialized when linking the feed.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(experience.id),
      }),
  });

  const addFeedToExperience = (
    feedId: string,
    options?: MutateOptions<
      AddFeedToExperienceMutation,
      QueryError,
      AddFeedToExperienceMutationVariables
    >
  ) => mutate({ input: { experienceId: experience.id, feedId } }, options);

  return { addFeedToExperience, ...rest };
};
